import axios from "axios";
import { getStoredAuthToken, removeStoredAuthToken } from "./authToken";
import pubsub from "sweet-pubsub";
import { captureApiError, captureUserAction } from "../sentry";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api'
const EXPECTED_BUSINESS_CONFLICT_CODES = new Set([
    "DUPLICATE_PO",
    "SIMILAR_ORDER_WARNING",
    "PO_LOCKED",
]);

// Export it so other files can use it
export const getBaseUrl = () => BASE_URL;

const defaults = {
    baseURL: `${BASE_URL}`,
    headers: () => ({
        "Accept": "application/json",
        Authorization: getStoredAuthToken()
            ? `Bearer ${getStoredAuthToken()}`
            : undefined,
    }),
    error: {
        code: "INTERNAL_ERROR",
        message:
            "Something went wrong. Please check your internet connection or contact our support.",
        status: 503,
        data: {},
    },
};

const api = (method: string, url: string, variables: any) => {
    return new Promise((resolve, reject) => {
        const fullUrl = `${defaults.baseURL}${url}`;

        // Capture API call for breadcrumbs
        captureUserAction('api_call', {
            method,
            url,
            has_data: !!variables,
        });

        axios({
            url: fullUrl,
            method,
            headers: defaults.headers(),
            params: method === "get" ? variables : undefined,
            data: method !== "get" ? variables : undefined,
        }).then(
            (response) => {
                resolve(response.data);
            },
            (error) => {

                // Capture API errors with Sentry
                if (error.response) {
                    const status = error.response.status;
                    const responseCode = error.response.data?.code;
                    const isExpectedBusinessConflict =
                        EXPECTED_BUSINESS_CONFLICT_CODES.has(responseCode);
                    
                    // Only capture 400 and 500 errors (as configured in backend)
                    if (status >= 400 && status < 500 && !isExpectedBusinessConflict) {
                        captureApiError(error, {
                            context: 'api_call',
                            method,
                            url,
                            variables,
                        });
                    } else if (status >= 500) {
                        captureApiError(error, {
                            context: 'api_call',
                            method,
                            url,
                            variables,
                        });
                    }
                    
                    if (status === 401) {
                        removeStoredAuthToken();
                        pubsub.emit("logout");
                        reject(error.response.data);
                    } else {
                        reject(error.response.data);
                    }
                } else {
                    // Network errors or other issues
                    captureApiError(error, {
                        context: 'network_error',
                        method,
                        url,
                        variables,
                    });
                    reject(defaults.error);
                }
            }
        );
    });
};

const parseBlobError = async (value: any) => {
    if (!(value instanceof Blob)) return value;
    try {
        const text = await value.text();
        return JSON.parse(text);
    } catch {
        return defaults.error;
    }
};

const apiBlob = async (method: string, url: string, variables: any, accept = "application/pdf, application/json"): Promise<Blob> => {
    const fullUrl = `${defaults.baseURL}${url}`;
    captureUserAction("api_call", {
        method,
        url,
        has_data: !!variables,
    });

    try {
        const response = await axios({
            url: fullUrl,
            method,
            headers: {
                ...defaults.headers(),
                Accept: accept,
            },
            params: method === "get" ? variables : undefined,
            data: method !== "get" ? variables : undefined,
            responseType: "blob",
        });
        return response.data;
    } catch (requestError: any) {
        if (requestError.response) {
            const status = requestError.response.status;
            const responseData = await parseBlobError(requestError.response.data);
            captureApiError(requestError, {
                context: "api_call",
                method,
                url,
                variables,
            });
            if (status === 401) {
                removeStoredAuthToken();
                pubsub.emit("logout");
            }
            throw responseData;
        }

        captureApiError(requestError, {
            context: "network_error",
            method,
            url,
            variables,
        });
        throw defaults.error;
    }
};

const apiMethods = {
    get: (url: string, variables?: any) => api("get", url, variables),
    post: (url: string, variables?: any) => api("post", url, variables),
    put: (url: string, variables?: any) => api("put", url, variables),
    patch: (url: string, variables?: any) => api("patch", url, variables),
    delete: (url: string, variables?: any) => api("delete", url, variables),
    postBlob: (url: string, variables?: any, accept?: string) => apiBlob("post", url, variables, accept),
};

export default apiMethods;
